import { useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import {
  CheckboxRadio,
  device,
  EmailInput,
  FormDescription,
  FormTitle,
  Segment,
  useEmailValidation,
  Row,
  Button,
  Column,
  theme,
  typography,
} from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import { rgba } from 'polished';
import React, { useContext, useEffect, useState } from 'react';
import type { RequestGdprDataInput } from '../../../../__generated__/globalTypes';
import type {
  RequestGdprData as IRequestGdprData,
  RequestGdprDataVariables,
} from '../../../graphql/gdpr/__generated__/RequestGdprData';
import { REQUEST_GDPR_DATA } from '../../../graphql/gdpr/requestGdprData';
import { PhoneAuthentication } from '../../phone-authentication/PhoneAuthentication';
import { GdprSuccess } from '../GdprSuccess';
import { useLogin } from '../useLogin';
import { CheckboxContainer } from './CheckboxContainer';

interface IProps {
  title: string;
  description: string;
  login_title: string;
  login_description: string;
  login_image: Prismic.Image;
}
const Host = styled.div``;

const FormSpacer = styled.div<{ smallTop?: boolean }>`
  height: 1px;
  background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
  ${(props) => (props.smallTop ? 'margin-top: 20px;' : 'margin-top: 40px;')};
  margin-bottom: 40px;
`;

const BodySmall = styled.div<{ bold?: boolean }>`
  ${typography.bodySmall}
  ${(props) => (props.bold ? 'font-weight:bold;margin-bottom:10px;' : '')};
`;

const RowBottom = styled(Row)`
  height: 100%;
  align-content: flex-end;
`;
const BigButton = styled(Button)`
  width: 100%;
`;
const MarginSegment = styled(Segment)`
  margin-left: -44px;
  margin-right: -44px;
  background-color: ${(props) => props.theme.shades.primary2};
  @media ${device.tablet} {
    margin-left: initial;
    margin-right: initial;
  }
`;
export function RequestGdprData({
  title,
  description,
  login_image,
  login_description,
  login_title,
}: IProps) {
  const { client } = useContext(LandsbankinnContext);
  const [finished, setFinished] = useState(false);
  const [gdprData, setGdprData] = useState<RequestGdprDataInput>({
    types: [],
    email: '',
    phoneNumber: '',
    formerSPCustomer: null,
  });

  const [emailRef, isEmailValid, errorMessage] = useEmailValidation(
    gdprData.email,
  );

  const [login, { data, loading, isLoggedIn, error }] = useLogin();

  useEffect(() => {
    if (data) {
      const { phoneNumber, email } = data.user;
      setGdprData({
        ...gdprData,
        phoneNumber: phoneNumber,
        email: email ?? '',
      });
    }
  }, [data]);

  const [requestData] = useMutation<IRequestGdprData, RequestGdprDataVariables>(
    REQUEST_GDPR_DATA,
    {
      client,
      context: {
        headers: { authorization: data?.token ? `Bearer ${data.token}` : '' },
      },
      onCompleted: () => setFinished(true),
    },
  );

  return (
    <Host>
      {!finished && !isLoggedIn && (
        <PhoneAuthentication
          title={login_title}
          description={login_description}
          image={login_image}
          login={login}
          loading={loading}
          error={error}
        />
      )}
      {!finished && isLoggedIn && (
        <MarginSegment>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              requestData({
                variables: {
                  data: gdprData,
                },
              });
            }}
          >
            <Row>
              <Column
                lg={{ width: 5 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <FormTitle text={title} />
              </Column>
              <Column
                lg={{ width: 6 / 12, offset: 1 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <FormDescription>{description}</FormDescription>
              </Column>
            </Row>
            <Row>
              <CheckboxContainer
                setGdprData={setGdprData}
                gdprData={gdprData}
              />
            </Row>
            <FormSpacer smallTop />
            <Row>
              <Column
                lg={{ width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <EmailInput
                  label="Netfang umsækjanda"
                  isValid={isEmailValid}
                  inputRef={emailRef}
                  errorMessage={errorMessage}
                  value={gdprData.email}
                  variant="noBorder"
                  labelVariant="aboveInput"
                  onChange={(e) =>
                    setGdprData({ ...gdprData, email: e.target.value })
                  }
                />
              </Column>
            </Row>
            <FormSpacer />
            <Row>
              <Column
                lg={{ width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <BodySmall bold>
                  Hefuru áður verið viðskiptavinur sparisjóðs eða
                  fjármögnunarfyrirtækis sem er nú hluti af Landsbankanum?
                </BodySmall>
                <BodySmall>
                  Um er að ræða: Sparisjóð Keflavíkur, Sparisjóð Norðurlands,
                  Sparisjóð Vestmannaeyja, SP-fjármögnun og Avant sem nú er
                  hluti af Landsbankanum.
                </BodySmall>
              </Column>
              <Column
                lg={{ width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <RowBottom>
                  <Column
                    lg={{ width: 1 / 5 }}
                    md={{ width: 12 / 12 }}
                    sm={{ width: 12 / 12 }}
                  >
                    <CheckboxRadio
                      label="Já"
                      id="formerSPCustomer"
                      checked={gdprData.formerSPCustomer === true}
                      onChange={() => {
                        setGdprData({ ...gdprData, formerSPCustomer: true });
                      }}
                    />
                  </Column>

                  <Column
                    lg={{ width: 1 / 5 }}
                    md={{ width: 12 / 12 }}
                    sm={{ width: 12 / 12 }}
                  >
                    <CheckboxRadio
                      label="Nei"
                      id="formerSPCustomer"
                      checked={gdprData.formerSPCustomer === false}
                      onChange={() => {
                        setGdprData({ ...gdprData, formerSPCustomer: false });
                      }}
                    />
                  </Column>

                  <Column
                    lg={{ width: 2 / 5 }}
                    md={{ width: 12 / 12 }}
                    sm={{ width: 12 / 12 }}
                  >
                    <CheckboxRadio
                      label="Veit ekki"
                      id="formerSPCustomer"
                      checked={gdprData.formerSPCustomer === undefined}
                      onChange={() => {
                        setGdprData({
                          ...gdprData,
                          formerSPCustomer: undefined,
                        });
                      }}
                    />
                  </Column>
                </RowBottom>
              </Column>
            </Row>
            <FormSpacer />
            <Row>
              <Column
                lg={{ offset: 6 / 12, width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <BigButton
                  bg={theme.colors.primary}
                  type="submit"
                  title="Senda beiðni"
                />
              </Column>
            </Row>
          </form>
        </MarginSegment>
      )}
      {finished && <GdprSuccess />}
    </Host>
  );
}
